import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { Signals, useSignaling } from '@/contexts/SignalingContext.tsx';

export interface Country {
  code: string;
  name: string;
  flag: string;
}

export interface GeoFilterState {
  mode: 'whitelist' | 'blacklist';
  countries: Country[];
  isEnabled: boolean;
  // todo: move to a separate context
  genderPreference: 'male' | 'female' | 'non-binary' | 'no-preference';
  genderFilterEnabled: boolean;
}

interface GeoFilterContextType {
  geoFilter: GeoFilterState;
  setFilterMode: (mode: 'whitelist' | 'blacklist') => void;
  addCountry: (country: Country) => void;
  removeCountry: (countryCode: string) => void;
  // setBlacklistCountry: (country: Country | null) => void;
  toggleGeoFilter: () => void;
  resetFilters: () => void;
  canAddWhitelistCountry: boolean;
  setGenderPreference: (gender: 'male' | 'female' | 'non-binary' | 'no-preference') => void;
  toggleGenderFilter: () => void;
}

const GeoFilterContext = createContext<GeoFilterContextType | undefined>(undefined);

const initialState: GeoFilterState = {
  mode: 'whitelist',
  countries: [],
  isEnabled: false,
  // todo: move to a separate context
  genderPreference: 'no-preference',
  genderFilterEnabled: false
};

export function GeoFilterProvider({ children }: { children: ReactNode }) {
  const [ geoFilter, setGeoFilter ] = useState<GeoFilterState>(initialState);
  const { sendSignal, isConnected } = useSignaling();

  const setFilterMode = (mode: 'whitelist' | 'blacklist') => {
    setGeoFilter(prev => ({
      ...prev,
      mode,
      countries: []
    }));
  };

  const addCountry = (country: Country) => {
    setGeoFilter(prev => {
      // Check if country already exists
      if (prev.countries.some(c => c.code === country.code)) {
        return prev;
      }

      // Limit to 3 countries // todo
      if (prev.countries.length >= 3) {
        return prev;
      }

      return {
        ...prev,
        countries: [ ...prev.countries, country ]
      };
    });
  };

  const removeCountry = (countryCode: string) => {
    setGeoFilter(prev => ({
      ...prev,
      countries: prev.countries.filter(c => c.code !== countryCode)
    }));
  };

  // const setBlacklistCountry = (country: Country | null) => {
  //   setGeoFilter(prev => ({
  //     ...prev,
  //     blacklistedCountry: country
  //   }));
  // };

  const toggleGeoFilter = () => {
    setGeoFilter(prev => ({
      ...prev,
      isEnabled: !prev.isEnabled
    }));
  };

  const resetFilters = () => {
    setGeoFilter(initialState);
  };

  // todo: move to a separate context
  const setGenderPreference = (gender: 'male' | 'female' | 'non-binary' | 'no-preference') => {
    setGeoFilter(prev => ({
      ...prev,
      genderPreference: gender
    }));
  };

  // todo: move to a separate context
  const toggleGenderFilter = () => {
    setGeoFilter(prev => ({
      ...prev,
      genderFilterEnabled: !prev.genderFilterEnabled
    }));
  };

  const canAddWhitelistCountry = geoFilter.mode === 'whitelist' &&
    geoFilter.countries.length < 3;

  useEffect(() => {
    if (!isConnected) return;

    sendSignal({
      signal: Signals.UpdateGeoFilter,
      geoFilter: {
        enabled: geoFilter.isEnabled,
        mode: geoFilter.mode,
        countries: geoFilter.countries.map(c => c.code)
      }
    });
  }, [ geoFilter, isConnected, sendSignal ]);

  return (
    <GeoFilterContext.Provider
      value={{
        geoFilter,
        setFilterMode,
        addCountry,
        removeCountry,
        // setBlacklistCountry,
        toggleGeoFilter,
        resetFilters,
        canAddWhitelistCountry,
        setGenderPreference,
        toggleGenderFilter
      }}
    >
      {children}
    </GeoFilterContext.Provider>
  );
}

export function useGeoFilter() {
  const context = useContext(GeoFilterContext);
  if (context === undefined) {
    throw new Error('useGeoFilter must be used within a GeoFilterProvider');
  }
  return context;
}

// Common countries list for the application
export const COUNTRIES: Country[] = [
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪' },
  { code: 'NO', name: 'Norway', flag: '🇳🇴' },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
  { code: 'FI', name: 'Finland', flag: '🇫🇮' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { code: 'CN', name: 'China', flag: '🇨🇳' },
  { code: 'IN', name: 'India', flag: '🇮🇳' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
  { code: 'RU', name: 'Russia', flag: '🇷🇺' },
  { code: 'PL', name: 'Poland', flag: '🇵🇱' },
  { code: 'TR', name: 'Turkey', flag: '🇹🇷' },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
  { code: 'EG', name: 'Egypt', flag: '🇪🇬' },
  { code: 'NG', name: 'Nigeria', flag: '🇳🇬' },
  { code: 'KE', name: 'Kenya', flag: '🇰🇪' },
  { code: 'TH', name: 'Thailand', flag: '🇹🇭' },
  { code: 'VN', name: 'Vietnam', flag: '🇻🇳' },
  { code: 'PH', name: 'Philippines', flag: '🇵🇭' },
  { code: 'ID', name: 'Indonesia', flag: '🇮🇩' },
  { code: 'MY', name: 'Malaysia', flag: '🇲🇾' },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' }
];